import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Skeleton,
  SkeletonLine,
  SkeletonRegion,
} from "@/components/ui/skeleton";
import { Pager } from "@/components/ui/pager";
import { SelectAllCheckbox } from "@/components/SelectAllCheckbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FULL_PAGE_SIZE } from "@/lib/paging";
import { cn } from "@jbnl/shared-ui";
import { momentLang } from "./venster";
import {
  DOELGROEP,
  MAIL_STATUS,
  mailStatusLabel,
  useHerinnering,
  useOntvangers,
  type Inschrijfperiode,
  type Ontvanger,
} from "./useInschrijfperiodes";

/**
 * The batch one person at a time, which is the form the question takes in January. A herinnering
 * leaves the aankondiging's stamp alone, so nothing here can make the card above say it was told.
 */
export function Ontvangers({
  seizoen,
  periode,
  gesloten,
}: {
  seizoen: { id: string };
  periode: Inschrijfperiode;
  /** A shut window refuses the send server-side, so the button never offers it. */
  gesloten: boolean;
}) {
  const { t } = useTranslation("inschrijfperiodes");
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          {t("ontvangers.tonen")}
        </Button>
      </div>
    );
  }

  return <Lijst seizoen={seizoen} periode={periode} gesloten={gesloten} />;
}

/**
 * Who a herinnering exists for, so the list arrives with them ticked rather than leaving eleven
 * rows to be hunted out of 165 by hand.
 */
function moetNagejaagd(ontvanger: Ontvanger) {
  return (
    ontvanger.lastStatus === null ||
    ontvanger.lastStatus === MAIL_STATUS.failed ||
    ontvanger.lastStatus === MAIL_STATUS.bounced ||
    ontvanger.lastStatus === MAIL_STATUS.complained
  );
}

function Lijst({
  seizoen,
  periode,
  gesloten,
}: {
  seizoen: { id: string };
  periode: Inschrijfperiode;
  gesloten: boolean;
}) {
  const { t } = useTranslation(["inschrijfperiodes", "common"]);
  const { data, isPending, error } = useOntvangers(periode.id, true);
  const herinneren = useHerinnering(seizoen.id, periode.id);

  const [zoek, setZoek] = useState("");
  const [pagina, setPagina] = useState(1);

  // Null is "nobody has touched the selection yet", which draws the default below rather than an
  // empty list. Kept apart from the default so a beheerder who clears every box keeps it clear.
  const [selectie, setSelectie] = useState<Set<string> | null>(null);

  const rijen = useMemo(() => data ?? [], [data]);

  const gekozen = useMemo(
    () => selectie ?? new Set(rijen.filter(moetNagejaagd).map((rij) => rij.to)),
    [selectie, rijen],
  );

  // Every row the search term matches, whatever page it falls on - "select all" and the count
  // beside it describe this whole set, not just the page rendered below.
  const zichtbaar = useMemo(() => {
    const term = zoek.trim().toLowerCase();
    if (!term) return rijen;

    return rijen.filter((rij) =>
      [rij.to, rij.name, rij.korps ?? ""].some((veld) =>
        veld.toLowerCase().includes(term),
      ),
    );
  }, [rijen, zoek]);

  const pageRijen = zichtbaar.slice(
    (pagina - 1) * FULL_PAGE_SIZE,
    pagina * FULL_PAGE_SIZE,
  );

  if (isPending) {
    return (
      <OntvangersSkeleton
        toonKorps={periode.targetGroup === DOELGROEP.korpsen}
      />
    );
  }

  if (error) {
    return <p className="text-sm text-destructive">{error.message}</p>;
  }

  if (rijen.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">{t("ontvangers.leeg")}</p>
    );
  }

  const zichtbaarGekozen = zichtbaar.filter((rij) =>
    gekozen.has(rij.to),
  ).length;
  const aantal = gekozen.size;

  function wissel(adres: string) {
    const volgende = new Set(gekozen);
    if (!volgende.delete(adres)) volgende.add(adres);
    setSelectie(volgende);
  }

  function wisselZichtbaar(alles: boolean) {
    const volgende = new Set(gekozen);
    for (const rij of zichtbaar) {
      if (alles) volgende.add(rij.to);
      else volgende.delete(rij.to);
    }
    setSelectie(volgende);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <Label
            htmlFor={`zoek-${periode.id}`}
            className="text-xs text-muted-foreground"
          >
            {t("ontvangers.zoek")}
          </Label>
          <Input
            id={`zoek-${periode.id}`}
            value={zoek}
            onChange={(event) => {
              setZoek(event.target.value);
              // A search narrows the set the pager counts, so page 2 of the old set would
              // otherwise land past the end of a shorter one.
              setPagina(1);
            }}
            className="w-64"
          />
        </div>

        <SelectAllCheckbox
          id={`alles-${periode.id}`}
          label={
            zoek ? t("ontvangers.allesGetoond") : t("common:selectAll.label")
          }
          total={zichtbaar.length}
          selected={zichtbaarGekozen}
          disabled={herinneren.isPending}
          onChange={wisselZichtbaar}
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>{t("ontvangers.naam")}</TableHead>
              {periode.targetGroup === DOELGROEP.korpsen && (
                <TableHead>{t("ontvangers.korps")}</TableHead>
              )}
              <TableHead>{t("ontvangers.email")}</TableHead>
              <TableHead>{t("ontvangers.laatsteMail")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRijen.map((rij) => (
              <TableRow key={rij.to}>
                <TableCell>
                  <Checkbox
                    checked={gekozen.has(rij.to)}
                    disabled={herinneren.isPending}
                    onCheckedChange={() => wissel(rij.to)}
                    aria-label={t("ontvangers.selecteer", { naam: rij.name })}
                  />
                </TableCell>
                <TableCell>{rij.name}</TableCell>
                {periode.targetGroup === DOELGROEP.korpsen && (
                  <TableCell className="text-muted-foreground">
                    {rij.korps}
                  </TableCell>
                )}
                <TableCell className="text-muted-foreground">
                  {rij.to}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {rij.lastStatus === null ? (
                    <span className="text-foreground">
                      {t("ontvangers.nogGeenMail")}
                    </span>
                  ) : rij.lastMailedOn ? (
                    t("ontvangers.laatsteMailMoment", {
                      status: mailStatusLabel(rij.lastStatus),
                      moment: momentLang(rij.lastMailedOn),
                    })
                  ) : (
                    mailStatusLabel(rij.lastStatus)
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Pager
        page={pagina}
        pageSize={FULL_PAGE_SIZE}
        totalCount={zichtbaar.length}
        onPageChange={setPagina}
      />

      {zichtbaar.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {t("ontvangers.geenTreffer", { term: zoek })}
        </p>
      )}

      <div className="flex items-center justify-end gap-3">
        {herinneren.error && (
          <p className="text-sm text-destructive">{herinneren.error.message}</p>
        )}
        {gesloten && (
          <p className="text-sm text-muted-foreground">
            {t("ontvangers.gesloten")}
          </p>
        )}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button disabled={gesloten || aantal === 0 || herinneren.isPending}>
              {herinneren.isPending
                ? t("common:actions.busy")
                : t("ontvangers.herinneren", { count: aantal })}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("ontvangers.herinnerTitle", { count: aantal })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("ontvangers.herinnerDescription", { periode: periode.name })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("common:actions.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                className={cn(buttonVariants())}
                onClick={() => herinneren.mutate([...gekozen])}
              >
                {t("ontvangers.herinnerActie", { count: aantal })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

/**
 * The roster before it has arrived. The headers are drawn for real - which columns there are
 * follows from the doelgroep, which the periode already knows - and only the rows are grey.
 */
function OntvangersSkeleton({ toonKorps }: { toonKorps: boolean }) {
  const { t } = useTranslation("inschrijfperiodes");

  return (
    <SkeletonRegion className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <SkeletonLine size="xs" className="w-48" />
          <Skeleton className="h-9 w-64" />
        </div>

        {/* The tri-state box beside the search, at the height its own row will have. */}
        <div className="flex h-9 items-center gap-2">
          <Skeleton className="size-4 rounded-sm" />
          <Skeleton className="h-3.5 w-36" />
        </div>
      </div>

      {/* The same six-row box the real list's own page draws - the list is paginated now, not
          scrolling, so there is no cap to match. */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>{t("ontvangers.naam")}</TableHead>
              {toonKorps && <TableHead>{t("ontvangers.korps")}</TableHead>}
              <TableHead>{t("ontvangers.email")}</TableHead>
              <TableHead>{t("ontvangers.laatsteMail")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 6 }, (_, rij) => (
              <TableRow key={rij}>
                <TableCell>
                  <Skeleton className="size-4 rounded-sm" />
                </TableCell>
                <TableCell>
                  <SkeletonLine className="w-40" />
                </TableCell>
                {toonKorps && (
                  <TableCell>
                    <SkeletonLine className="w-32" />
                  </TableCell>
                )}
                <TableCell>
                  <SkeletonLine className="w-52" />
                </TableCell>
                <TableCell>
                  <SkeletonLine className="w-36" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </SkeletonRegion>
  );
}
